import { useEffect } from "react";

/**
 * Manages per-page <head> for SEO: title, description, canonical, robots, FAQ schema.
 * Restores previous values on unmount so navigation between pages stays clean.
 */
export const SEOHead = ({ title, description, canonical, faqs, noIndex = false }) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    // Helper to set-or-create a meta tag
    const ensureMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev, created };
    };

    const ensureLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return { el, prev, created };
    };

    const desc = ensureMeta("name", "description", description);
    const robotsContent = noIndex ? "noindex, nofollow" : "index, follow";
    const robots = ensureMeta("name", "robots", robotsContent);
    const canon = ensureLink("canonical", canonical);

    // FAQ Schema (only when visible page contains matching FAQs)
    let faqScript = null;
    if (faqs && faqs.length > 0 && !noIndex) {
      faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.id = "faq-schema-page";
      faqScript.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      });
      document.head.appendChild(faqScript);
    }

    return () => {
      document.title = prevTitle;
      if (desc.created) desc.el.remove();
      else if (desc.prev !== null) desc.el.setAttribute("content", desc.prev);
      if (robots.created) robots.el.remove();
      else if (robots.prev !== null) robots.el.setAttribute("content", robots.prev);
      if (canon.created) canon.el.remove();
      else if (canon.prev !== null) canon.el.setAttribute("href", canon.prev);
      if (faqScript) faqScript.remove();
    };
  }, [title, description, canonical, faqs, noIndex]);

  return null;
};
